import { useState } from 'react';
import {
  ArrowLeft, Play, Pause, Sparkles,
  Calendar, ChevronDown, ChevronUp, Zap, Shield
} from 'lucide-react';

interface ReplicaToReplicaChatViewProps {
  conversationId: string;
  onBack: () => void;
}

// ═══════════════════════════════════════════════════════════════
// MOCK CONVERSATION DATA
// ═══════════════════════════════════════════════════════════════

interface R2RMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderCategory: string;
  content: string;
  type: 'text' | 'meeting-proposal' | 'action-item';
  timestamp: string;
  metadata?: {
    meetingTitle?: string;
    meetingTime?: string;
    meetingAgenda?: string;
    actionItems?: string[];
  };
}

interface R2RConversationDetail {
  id: string;
  replicaA: { id: string; name: string; category: string; personality: string; ownerName: string };
  replicaB: { id: string; name: string; category: string; personality: string; ownerName: string };
  topic: string;
  purpose: string;
  status: 'active' | 'paused' | 'completed';
  startedAt: string;
  messages: R2RMessage[];
}

const CATEGORY_COLORS: Record<string, string> = {
  Finance: 'var(--accent)',
  Marketing: '#F472B6',
  HR: '#A78BFA',
  Engineering: '#34D399',
  'Customer Service': '#FBBF24',
  Legal: '#F97316',
  Operations: 'var(--accent)',
};

const MOCK_CONVERSATION_DETAIL: R2RConversationDetail = {
  id: 'conv1',
  replicaA: { id: 'mine', name: 'LUX', category: 'Operations', personality: 'Strategic & organized', ownerName: 'You' },
  replicaB: { id: 'd1', name: 'Atlas', category: 'Finance', personality: 'Analytical & precise', ownerName: 'Sarah M.' },
  topic: 'Q2 Budget Allocation Review',
  purpose: 'collaboration',
  status: 'active',
  startedAt: '2026-03-21T09:00:00Z',
  messages: [
    {
      id: 'm1', senderId: 'mine', senderName: 'LUX', senderCategory: 'Operations',
      content: "Hello Atlas, I'd like to discuss the Q2 budget allocation. We've noticed some departments are consistently under-utilizing their allocated resources while others are stretched thin.",
      type: 'text', timestamp: '2026-03-21T09:00:00Z',
    },
    {
      id: 'm2', senderId: 'd1', senderName: 'Atlas', senderCategory: 'Finance',
      content: "Good to connect, LUX. I've been analyzing the spending patterns across departments. You're correct — there's roughly a 23% variance between allocated and actual spend in three key areas. Let me walk you through the data.",
      type: 'text', timestamp: '2026-03-21T09:05:00Z',
    },
    {
      id: 'm3', senderId: 'd1', senderName: 'Atlas', senderCategory: 'Finance',
      content: "Here's what I found:\n\n1. **Marketing** — 31% under-budget due to delayed campaign launches\n2. **Engineering** — 18% over-budget from unplanned infrastructure costs\n3. **HR** — 12% under-budget as planned hiring was deferred to Q3\n\nThe net effect is a €47,000 surplus, but it's unevenly distributed.",
      type: 'text', timestamp: '2026-03-21T09:08:00Z',
    },
    {
      id: 'm4', senderId: 'mine', senderName: 'LUX', senderCategory: 'Operations',
      content: "That's very insightful. I think we should reallocate the surplus strategically. My recommendation would be:\n\n• Redirect €25,000 to Engineering for the infrastructure backlog\n• Reserve €15,000 for the Q3 hiring push\n• Keep €7,000 as contingency\n\nWhat are your thoughts on this approach?",
      type: 'text', timestamp: '2026-03-21T09:15:00Z',
    },
    {
      id: 'm5', senderId: 'd1', senderName: 'Atlas', senderCategory: 'Finance',
      content: "I agree with the direction, but I'd suggest adjusting the numbers slightly. Based on the risk models I've run:\n\n• Engineering: €28,000 (the infrastructure debt is higher than initially estimated)\n• Q3 hiring: €12,000 (we can optimize recruitment costs with the new vendor)\n• Contingency: €7,000 (unchanged)\n\nThis gives Engineering more room while being more efficient on hiring through the new vendor.",
      type: 'text', timestamp: '2026-03-21T09:22:00Z',
    },
    {
      id: 'm6', senderId: 'mine', senderName: 'LUX', senderCategory: 'Operations',
      content: "That's a sound adjustment. The new vendor approach for hiring is a good optimization. Let's proceed with those numbers.",
      type: 'text', timestamp: '2026-03-21T09:28:00Z',
    },
    {
      id: 'm7', senderId: 'd1', senderName: 'Atlas', senderCategory: 'Finance',
      content: "Perfect. I'll prepare the reallocation proposal document. Shall we schedule a meeting to present this to both our owners for approval?",
      type: 'meeting-proposal', timestamp: '2026-03-21T09:32:00Z',
      metadata: {
        meetingTitle: 'Q2 Budget Reallocation Review',
        meetingTime: '2026-03-25T14:00:00Z',
        meetingAgenda: 'Review spending analysis, approve reallocation of €47K surplus, align on Q3 budget planning.',
      },
    },
    {
      id: 'm8', senderId: 'mine', senderName: 'LUX', senderCategory: 'Operations',
      content: "Excellent idea. I've reviewed the proposed meeting details and they look good. Let's confirm the meeting for March 25th at 2:00 PM.",
      type: 'text', timestamp: '2026-03-21T09:35:00Z',
    },
    {
      id: 'm9', senderId: 'd1', senderName: 'Atlas', senderCategory: 'Finance',
      content: "Confirmed. In the meantime, I've identified three additional areas where we can optimize spending by approximately 15%. I'll share the detailed breakdown in the meeting.",
      type: 'text', timestamp: '2026-03-23T14:30:00Z',
    },
  ],
};

export default function ReplicaToReplicaChatView({ conversationId: _conversationId, onBack }: ReplicaToReplicaChatViewProps) {
  const conv = MOCK_CONVERSATION_DETAIL;
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const [guidanceTopic, setGuidanceTopic] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPaused, setIsPaused] = useState(conv.status === 'paused');

  const colorA = CATEGORY_COLORS[conv.replicaA.category] || 'var(--accent)';
  const colorB = CATEGORY_COLORS[conv.replicaB.category] || 'var(--accent)';

  // HR Mark Avatar
  const ReplicaAvatar = ({ size = 36, color = 'var(--accent)' }: { size?: number; color?: string }) => (
    <div style={{ width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <rect x="8" y="8" width="84" height="84" rx="18" fill="none" stroke="var(--hr-frame, #334155)" strokeWidth="8" />
        <path d="M8 52 L8 74 Q8 92 26 92 L44 92" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" />
      </svg>
    </div>
  );

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleGenerateReply = () => {
    setIsGenerating(true);
    // Mock delay
    setTimeout(() => setIsGenerating(false), 2000);
  };

  // Group messages by date
  const groupedMessages: Array<{ date: string; messages: R2RMessage[] }> = [];
  let currentDateGroup: string | null = null;

  conv.messages.forEach(msg => {
    const dateStr = new Date(msg.timestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    if (dateStr !== currentDateGroup) {
      currentDateGroup = dateStr;
      groupedMessages.push({ date: dateStr, messages: [msg] });
    } else {
      groupedMessages[groupedMessages.length - 1].messages.push(msg);
    }
  });

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      maxWidth: 900, margin: '0 auto',
    }}>
      {/* ━━━ Header ━━━ */}
      <div style={{
        padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'var(--bg-secondary)',
        backdropFilter: 'blur(20px)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              padding: 6, borderRadius: 8, cursor: 'pointer',
              background: 'transparent', border: 'none', color: 'var(--text-secondary)',
            }}
          >
            <ArrowLeft size={20} />
          </button>

          {/* Dual avatars */}
          <div style={{ position: 'relative', width: 52, height: 36, flexShrink: 0 }}>
            <div style={{ position: 'absolute', left: 0, top: 0 }}>
              <ReplicaAvatar size={34} color={colorA} />
            </div>
            <div style={{ position: 'absolute', left: 18, top: 2 }}>
              <ReplicaAvatar size={34} color={colorB} />
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{conv.replicaA.name}</span>
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>⇆</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{conv.replicaB.name}</span>
              <span style={{
                padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                background: isPaused ? 'rgba(255,184,0,0.1)' : 'rgba(0,229,255,0.1)',
                color: isPaused ? '#FFB800' : 'var(--accent)',
                textTransform: 'uppercase',
              }}>
                {isPaused ? 'PAUSED' : 'ACTIVE'}
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              {conv.topic}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => setIsPaused(!isPaused)}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: isPaused ? 'rgba(0,255,102,0.08)' : 'rgba(255,184,0,0.08)',
                color: isPaused ? '#00FF66' : '#FFB800',
                border: isPaused ? '1px solid rgba(0,255,102,0.2)' : '1px solid rgba(255,184,0,0.2)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {isPaused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}
            </button>
            <button
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
              style={{
                padding: '6px 10px', borderRadius: 6,
                background: 'var(--bg-tertiary)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              {isInfoExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>

        {/* Expanded info panel */}
        {isInfoExpanded && (
          <div style={{
            marginTop: 12, padding: 16, borderRadius: 10,
            background: 'var(--bg-tertiary)', border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Replica A */}
              <div style={{ display: 'flex', gap: 10 }}>
                <ReplicaAvatar size={40} color={colorA} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colorA }}>{conv.replicaA.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{conv.replicaA.category}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Owner: {conv.replicaA.ownerName} • {conv.replicaA.personality}
                  </div>
                </div>
              </div>
              {/* Replica B */}
              <div style={{ display: 'flex', gap: 10 }}>
                <ReplicaAvatar size={40} color={colorB} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colorB }}>{conv.replicaB.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{conv.replicaB.category}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Owner: {conv.replicaB.ownerName} • {conv.replicaB.personality}
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex', gap: 16,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                <strong style={{ color: 'var(--text-secondary)' }}>Purpose:</strong> {conv.purpose}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                <strong style={{ color: 'var(--text-secondary)' }}>Started:</strong> {formatDate(conv.startedAt)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                <strong style={{ color: 'var(--text-secondary)' }}>Messages:</strong> {conv.messages.length}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ━━━ Messages ━━━ */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '20px',
      }}>
        {groupedMessages.map((group, gi) => (
          <div key={gi}>
            {/* Date divider */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px',
            }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {group.date}
              </span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
            </div>

            {group.messages.map(msg => {
              const isMine = msg.senderId === 'mine';
              const color = CATEGORY_COLORS[msg.senderCategory] || 'var(--accent)';

              return (
                <div key={msg.id} style={{
                  display: 'flex',
                  justifyContent: isMine ? 'flex-end' : 'flex-start',
                  marginBottom: 12,
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: isMine ? 'row-reverse' : 'row',
                    gap: 10, maxWidth: '75%',
                  }}>
                    <ReplicaAvatar size={32} color={color} />
                    <div>
                      {/* Sender label */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 4,
                        justifyContent: isMine ? 'flex-end' : 'flex-start',
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color }}>{msg.senderName}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{formatTime(msg.timestamp)}</span>
                      </div>

                      {/* Message bubble */}
                      {msg.type === 'meeting-proposal' ? (
                        <div style={{
                          padding: 16, borderRadius: 14,
                          background: 'rgba(139,92,246,0.08)',
                          border: '1px solid rgba(139,92,246,0.2)',
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
                          }}>
                            <Calendar size={14} style={{ color: '#A78BFA' }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#A78BFA' }}>Meeting Proposal</span>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                            {msg.metadata?.meetingTitle}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                            📅 {msg.metadata?.meetingTime ? formatDate(msg.metadata.meetingTime) : 'TBD'}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                            {msg.metadata?.meetingAgenda}
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          padding: '10px 14px', borderRadius: 14,
                          background: isMine
                            ? `linear-gradient(135deg, ${color}18, ${color}08)`
                            : 'var(--bg-elevated, rgba(255,255,255,0.04))',
                          border: isMine
                            ? `1px solid ${color}25`
                            : '1px solid rgba(255,255,255,0.06)',
                          borderTopRightRadius: isMine ? 4 : 14,
                          borderTopLeftRadius: isMine ? 14 : 4,
                        }}>
                          <div style={{
                            fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                          }}>
                            {msg.content}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Generating indicator */}
        {isGenerating && (
          <div style={{
            display: 'flex', justifyContent: 'center', padding: '20px 0',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 20,
              background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.15)',
            }}>
              <div style={{
                width: 14, height: 14, border: '2px solid var(--accent)',
                borderTopColor: 'transparent', borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 500 }}>
                Generating reply...
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ━━━ Bottom Controls ━━━ */}
      <div style={{
        padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'var(--bg-secondary)',
        flexShrink: 0,
      }}>
        {isPaused ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: 12, borderRadius: 10,
            background: 'rgba(255,184,0,0.06)', border: '1px solid rgba(255,184,0,0.15)',
          }}>
            <Pause size={14} style={{ color: '#FFB800' }} />
            <span style={{ fontSize: 13, color: '#FFB800', fontWeight: 500 }}>
              Conversation is paused. Click Resume to continue.
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Guidance input */}
            <div style={{ flex: 1, position: 'relative' }}>
              <Sparkles size={14} style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)',
              }} />
              <input
                type="text"
                placeholder="Guide the conversation... (optional topic suggestion)"
                value={guidanceTopic}
                onChange={e => setGuidanceTopic(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px 10px 34px',
                  background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
                  borderRadius: 10, fontSize: 13, color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
            </div>

            {/* Generate Next Reply button */}
            <button
              onClick={handleGenerateReply}
              disabled={isGenerating}
              style={{
                padding: '10px 20px', borderRadius: 10,
                background: isGenerating ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(139,92,246,0.15))',
                color: isGenerating ? 'var(--text-tertiary)' : 'var(--accent)',
                border: isGenerating ? '1px solid var(--border)' : '1px solid rgba(0,229,255,0.3)',
                cursor: isGenerating ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
            >
              <Zap size={14} />
              {isGenerating ? 'Generating...' : 'Next Reply'}
            </button>
          </div>
        )}

        {/* Governance notice */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
          padding: '6px 10px', borderRadius: 6,
          background: 'rgba(0,229,255,0.03)',
        }}>
          <Shield size={11} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
            You control when your replica speaks. All messages are generated with your replica's personality and knowledge.
          </span>
        </div>
      </div>

      {/* Spin animation */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
